/**
 * dealWideChildren — shared plumbing for the "Whole deal" scope toggle on the
 * Lease/Deeds and Wells tabs.
 *
 * There is no per-deal endpoint for the sub-child records (recordings, tracts,
 * well tracts) — they are only listed by their immediate parent id. So the
 * deal-wide roll-up loads the deal's masters (already in tab state) and fans
 * out one child fetch per master (Promise.allSettled so a single bad parent
 * doesn't sink the whole view), tagging each returned row with a friendly
 * parent label. This is the same fan-out that used to live in the standalone
 * DealChildrenTab; both tabs now share it.
 *
 * Note: this is O(number of masters) HTTP calls. Fine for typical deal sizes;
 * if deals get large, add a `?deal_id=` filter to the collection endpoints in
 * de-hub and collapse each section to a single call.
 */

// The synthetic column that carries the friendly parent label on each row.
// ReadOnlyChildTable renders it as a leading, fixed column.
export const PARENT_KEY = "_parent";

const rowsOf = (resp) => resp?.data?.data || [];
const settledRows = (s) => (s.status === "fulfilled" ? rowsOf(s.value) : []);

/**
 * Fan a per-master child fetch out across every master, tagging each child
 * row with its parent's friendly label under {@link PARENT_KEY}.
 *
 * @param {object}   args
 * @param {Array}    args.masters     — the deal's master rows (lease/deeds or wells)
 * @param {Function} args.fetchChild  — (master) => Promise<axiosResponse> for that master's children
 * @param {Function} args.labelOf     — (master) => string friendly parent label
 * @returns {Promise<Array>}          — flattened child rows, each with PARENT_KEY set
 */
export async function fanOutChildren({ masters, fetchChild, labelOf }) {
  const list = Array.isArray(masters) ? masters : [];
  const settled = await Promise.allSettled(list.map((m) => fetchChild(m)));
  return list.flatMap((m, i) =>
    settledRows(settled[i]).map((r) => ({ ...r, [PARENT_KEY]: labelOf(m) }))
  );
}
